.root {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--tuna-spacing-1);
  width: 100%;
}

.svg {
  width: 100%;
  height: 16rem;
  background: var(--tuna-surface-elevated);
  border-radius: var(--tuna-shape-borderRadius);
  border: 1px solid var(--tuna-border-subtle);
  /*
   * Joni's touch ruling (D7): one finger on empty space pans the roll, which scrolls sideways on a phone;
   * a tap there still adds a note. One finger on a note moves it, so the notes keep the gesture.
   */
  touch-action: pan-x pan-y;
}

.compact {
  height: 6rem;
}

/* Melody toolkit M2: the editable roll is a tab stop for its shortcuts, so it shows where focus is */
.svg:focus {
  outline: none;
}

.svg:focus-visible {
  outline: 2px solid var(--tuna-palette-primary-main);
  outline-offset: 2px;
}

/*
 * Row shading by key. The key is a HINT (§2): an out-of-key row is drawn quieter, never marked as
 * wrong, and a note may sit on it freely. WCAG 1.4.1 - the tonic also carries a data-row attribute
 * and a heavier top edge, so the three states are not distinguished by fill alone.
 */
.inKeyRow {
  fill: var(--tuna-surface-elevated);
}

/*
 * `--tuna-border-subtle`, not `--tuna-palette-grey-300`: the raw grey ramp is defined once and is
 * NOT redefined for dark mode, so a literal grey-300 would paint a bright band across a dark roll.
 */
.outOfKeyRow {
  fill: var(--tuna-border-subtle);
  opacity: 0.45;
}

.tonicRow {
  fill: var(--tuna-palette-info-light);
  opacity: 0.5;
}

.slotLine {
  stroke: var(--tuna-border-subtle);
  stroke-width: 0.15;
  opacity: 0.6;
}

.barLine {
  stroke: var(--tuna-content-secondary);
  stroke-width: 0.4;
  opacity: 0.7;
}

.insertSurface {
  fill: transparent;
  cursor: crosshair;
}

.note {
  fill: var(--tuna-palette-info-main);
  rx: 0.6;
}

.noteSelected {
  fill: var(--tuna-palette-info-dark);
  stroke: var(--tuna-content-primary);
  stroke-width: 0.4;
}

/* Phase 2 feedback: a marked note is hollow and dashed, so it reads without colour */
.noteMarked {
  fill: transparent;
  stroke: var(--tuna-content-primary);
  stroke-width: 0.5;
  stroke-dasharray: 1 0.6;
}

/* Over the plot, so the labels keep their size however the viewBox stretches */
.markLayer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.markLabel {
  position: absolute;
  transform: translateY(-100%);
  padding: 0 var(--tuna-spacing-0_5);
  font-size: var(--tuna-typography-caption-fontSize);
  font-weight: 600;
  line-height: 1;
  color: var(--tuna-content-primary);
}

/*
 * Theory cues (melody toolkit M3; spec §5.3; Joni's Q1 answer): the existing note tokens, never
 * outOfScale's orange, and nothing red. The shapes below carry each role without colour (WCAG 1.4.1).
 */
.roleChordTone {
  fill: var(--tuna-notes-inScale);
}

.rolePassing {
  fill: var(--tuna-notes-main);
}

.roleBlue {
  fill: var(--tuna-notes-root);
}

.roleOutside {
  fill: none;
  stroke: var(--tuna-notes-main);
  stroke-width: 1.5px;
  stroke-dasharray: 4 3;
  vector-effect: non-scaling-stroke;
}

/* A tension: an inner stripe through the bar */
.cueStripe {
  fill: var(--tuna-surface-elevated);
  pointer-events: none;
}

/* A suspension, a lean or an avoid note: an arrow toward where it goes */
.cueArrow {
  fill: var(--tuna-surface-elevated);
  pointer-events: none;
}

/* A blue note: a notch out of the bar's corner */
.cueNotch {
  fill: var(--tuna-surface-elevated);
  pointer-events: none;
}

/* Spec §5.3: strong beats shaded on the grid */
.strongBeat {
  fill: var(--tuna-border-subtle);
  opacity: 0.35;
  pointer-events: none;
}

/* "What fits here", while a note is dragged: chord tones strongest, tensions lighter, avoid notes hatched */
.fitChord {
  fill: var(--tuna-notes-inScale);
  opacity: 0.22;
}

.fitTension {
  fill: var(--tuna-notes-inScale);
  opacity: 0.1;
}

.fitAvoid {
  fill: var(--tuna-notes-main);
  opacity: 0.18;
  stroke: var(--tuna-notes-main);
  stroke-width: 1px;
  stroke-dasharray: 2 2;
  vector-effect: non-scaling-stroke;
}

/* D8: the Advanced degree numbers, in HTML over the stretched plot so they keep their size */
.degreeLayer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.degreeLabel {
  position: absolute;
  display: flex;
  align-items: center;
  padding: 0 var(--tuna-spacing-0_5);
  font-size: var(--tuna-typography-caption-fontSize);
  font-weight: 600;
  line-height: 1;
  color: var(--tuna-content-primary);
}

/*
 * Each number on a chip of its own: legible over a filled bar, a hollow outside note, a tension's stripe, and
 * the grid where a thin row leaves the number taller than its bar (Joni, M1–M5 review)
 */
.degreeLabel > span {
  padding: var(--tuna-spacing-0_5);
  border-radius: var(--tuna-shape-borderRadius);
  background: var(--tuna-surface-elevated);
}

.noteGroup {
  cursor: pointer;
  outline: none;
  touch-action: none;
}

.noteGroup:focus-visible .note {
  stroke: var(--tuna-content-primary);
  stroke-width: 0.5;
}

/* The offset nub: drag it to move the bar's drawn start off its gridline. */
.offsetHandle {
  fill: var(--tuna-feedback-warning);
  cursor: ew-resize;
}

.resizeHandle {
  fill: var(--tuna-content-secondary);
  cursor: ew-resize;
}

.playhead {
  stroke: var(--tuna-feedback-success);
  stroke-width: 0.5;
}

.cursor {
  stroke: var(--tuna-content-primary);
  stroke-width: 0.4;
  stroke-dasharray: 2 1.5;
}

/*
 * Melody toolkit M6: the box a drag on the empty roll selects by; dashed, so it reads without colour. In screen pixels,
 * as .roleOutside, so the stretched viewBox draws every edge the same (Joni's #3013 review, P3 5)
 */
.boxSelect {
  fill: transparent;
  stroke: var(--tuna-content-primary);
  stroke-width: 1.5px;
  stroke-dasharray: 4 3;
  vector-effect: non-scaling-stroke;
  pointer-events: none;
}

.empty {
  color: var(--tuna-content-muted);
  font-size: var(--tuna-typography-body2-fontSize);
  padding: var(--tuna-spacing-2);
}

/* Ghost options (melody toolkit M4; spec §6): outlines at half strength over the melody, never interactive */
.ghostLayer {
  pointer-events: none;
}

/* The note and its cue shapes together: half strength, and the fade (Joni's review) */
.ghostGroup {
  opacity: 0.5;
}

/* An outline, never filled like a written note */
.ghostNote {
  fill: transparent;
  stroke: var(--tuna-content-primary);
  stroke-width: 0.5;
  stroke-dasharray: 1 0.6;
  rx: 0.6;
}

/* A ghost's role, on its outline: the same colours a written note's fill carries */
.ghostChordTone {
  stroke: var(--tuna-notes-inScale);
}

.ghostPassing {
  stroke: var(--tuna-notes-main);
}

.ghostBlue {
  stroke: var(--tuna-notes-root);
}

/* The written melody under a ghost option */
.noteDimmed {
  opacity: 0.25;
}

/* Only where motion is allowed: the editor leaves this class off with reduced motion (spec §10) */
.ghostFade {
  animation: ghost-fade-in 200ms ease-out;
}

@keyframes ghost-fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 0.5;
  }
}

/* Melody toolkit M8a; spec §5.7: the singer's range, a quiet band behind the rows' own shading */
.rangeBand {
  fill: var(--tuna-palette-info-main);
  opacity: 0.08;
  pointer-events: none;
}

.rangeLabelLayer {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.rangeLabel {
  position: absolute;
  left: var(--tuna-spacing-1);
  font-size: var(--tuna-typography-caption-fontSize);
  color: var(--tuna-content-secondary);
}
